import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Headphones, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { ContactAgent } from '@workspace/types';
import { mesaApi } from '../api/mesaApi';
import { contactAgentError } from '../../contact-agent/api/contactAgentScope';
import { ContactAgentPage, ContactAgentPageHeader } from '../../contact-agent/components/ContactAgentPageHeader';
import { ContactAgentTenantPicker } from '../../contact-agent/components/ContactAgentTenantPicker';
import { useContactAgentTenant } from '../../contact-agent/hooks/useContactAgentTenant';

export function MesaDashboardPage() {
  const { tenantId, tenants, isSuperAdmin, ready, selectTenant } = useContactAgentTenant();
  const [agents, setAgents] = useState<ContactAgent[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!ready) {
      return;
    }
    let cancelled = false;
    setError('');
    void mesaApi
      .listAgents()
      .then(list => {
        if (!cancelled) {
          setAgents(list);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(contactAgentError(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ready, tenantId]);

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        title='Mesa de ayuda'
        description='Agentes de atención N1 por bot. Los operadores trabajan en el inbox.'
        showBack={false}
        toolbar={
          isSuperAdmin ? (
            <ContactAgentTenantPicker tenants={tenants} tenantId={tenantId} onChange={selectTenant} />
          ) : null
        }
        actions={
          <Button asChild>
            <Link to='/admin/mesa/agents/new'>
              <Plus className='mr-2 h-4 w-4' />
              Nuevo agente
            </Link>
          </Button>
        }
      />

      {error ? <p className='text-sm text-destructive'>{error}</p> : null}

      <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
        {agents.map(agent => (
          <Card key={agent.id} className='rounded-2xl shadow-sm'>
            <CardHeader className='flex flex-row items-start justify-between gap-2'>
              <div>
                <CardTitle className='text-lg'>{agent.name}</CardTitle>
                <p className='text-sm text-muted-foreground'>{agent.bot?.name ?? agent.botId}</p>
              </div>
              <Badge variant='secondary'>Mesa</Badge>
            </CardHeader>
            <CardContent className='space-y-3'>
              <p className='text-sm text-muted-foreground line-clamp-2'>
                {agent.objective || 'Sin objetivo definido.'}
              </p>
              <p className='text-xs text-muted-foreground'>
                {agent.toolsEnabled.length} herramienta{agent.toolsEnabled.length === 1 ? '' : 's'} activa
                {agent.toolsEnabled.length === 1 ? '' : 's'}
              </p>
              <Button variant='outline' size='sm' asChild>
                <Link to={`/admin/mesa/agents/${agent.id}`}>Editar agente</Link>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {ready && agents.length === 0 && !error ? (
        <Card className='rounded-2xl border-dashed'>
          <CardContent className='flex flex-col items-center gap-3 py-12 text-center'>
            <Headphones className='h-10 w-10 text-muted-foreground' />
            <p className='text-muted-foreground'>Todavía no hay agentes de mesa configurados.</p>
            <Button asChild>
              <Link to='/admin/mesa/agents/new'>Crear primer agente</Link>
            </Button>
          </CardContent>
        </Card>
      ) : null}
    </ContactAgentPage>
  );
}
